import { zodResolver } from "@hookform/resolvers/zod";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import {
	ExternalLink,
	Eye,
	Info,
	Loader2,
	Mail,
	Palette,
	Pin,
	Send,
	Users,
	X,
} from "lucide-react";
import type * as React from "react";
import { createContext, use, useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { RevenueProviderSelect } from "@/components/agents/revenue-provider-select";
import { BrandButton } from "@/components/brand/brand-button";
import { BrandInput } from "@/components/brand/brand-input";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@/components/ui/form";
import { trackEvent } from "@/lib/analytics";
import {
	REVENUE_PROVIDER_CONFIG,
	REVENUE_PROVIDER_IDS,
	type RevenueProviderId,
} from "@/lib/revenue-provider-config";
import {
	STARTUP_LISTING_PLANS,
	type StartupListingFeatureIcon,
	type StartupListingPlanId,
} from "@/lib/startup-listing-pricing";
import { toast } from "@/lib/toast";
import { cn } from "@/lib/utils";

// --- Context ---

interface SellStartupDialogContextValue {
	openSellStartupDialog: () => void;
	closeSellStartupDialog: () => void;
	isOpen: boolean;
}

const SellStartupDialogContext =
	createContext<SellStartupDialogContextValue | null>(null);

export function useSellStartupDialog() {
	const context = use(SellStartupDialogContext);
	if (!context) {
		throw new Error(
			"useSellStartupDialog must be used within a SellStartupDialogProvider",
		);
	}
	return context;
}

interface SellStartupDialogProviderProps {
	children: React.ReactNode;
}

export function SellStartupDialogProvider({
	children,
}: SellStartupDialogProviderProps) {
	const [isOpen, setIsOpen] = useState(false);

	const openSellStartupDialog = useCallback(() => setIsOpen(true), []);
	const closeSellStartupDialog = useCallback(() => setIsOpen(false), []);

	return (
		<SellStartupDialogContext.Provider
			value={{ openSellStartupDialog, closeSellStartupDialog, isOpen }}
		>
			{children}
			<SellStartupDialogContent open={isOpen} onOpenChange={setIsOpen} />
		</SellStartupDialogContext.Provider>
	);
}

// --- Schema ---

const sellStartupSchema = z.object({
	provider: z.enum(REVENUE_PROVIDER_IDS),
	apiKey: z.string().trim().optional(),
	askingPriceUsd: z
		.string()
		.trim()
		.min(1, "Asking price is required")
		.refine((value) => {
			const parsed = Number(value);
			return Number.isFinite(parsed) && parsed > 0;
		}, "Enter a valid asking price"),
	profitMarginPct: z
		.string()
		.trim()
		.min(1, "Profit margin is required")
		.refine((value) => {
			const parsed = Number(value);
			return Number.isFinite(parsed) && parsed >= 0 && parsed <= 100;
		}, "Enter a margin between 0 and 100"),
});

type SellStartupForm = z.infer<typeof sellStartupSchema>;

const DEFAULT_VALUES: SellStartupForm = {
	provider: "stripe",
	apiKey: "",
	askingPriceUsd: "",
	profitMarginPct: "",
};

const PLAN_FEATURE_ICONS: Record<
	StartupListingFeatureIcon,
	React.ComponentType<{ className?: string }>
> = {
	visitors: Users,
	color: Palette,
	newsletter: Mail,
	views: Eye,
	pin: Pin,
	match: Send,
};

// --- Dialog ---

function SellStartupDialogContent({
	open,
	onOpenChange,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
}) {
	const [selectedPlan, setSelectedPlan] = useState<StartupListingPlanId | null>(
		null,
	);
	const form = useForm<SellStartupForm>({
		resolver: zodResolver(sellStartupSchema),
		mode: "onBlur",
		reValidateMode: "onChange",
		defaultValues: DEFAULT_VALUES,
	});

	const handleOpenChange = useCallback(
		(nextOpen: boolean) => {
			if (!nextOpen) {
				form.reset(DEFAULT_VALUES);
				setSelectedPlan(null);
			}
			onOpenChange(nextOpen);
		},
		[onOpenChange, form],
	);

	const onSubmit = async (values: SellStartupForm) => {
		if (!selectedPlan) {
			toast.error("Choose a listing plan to continue");
			return;
		}

		try {
			const response = await fetch(
				"/api/stripe/startup-listing-checkout-public",
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Accept: "application/json",
					},
					body: JSON.stringify({
						plan: selectedPlan,
						provider: values.provider,
						apiKey: values.apiKey?.trim() || undefined,
						askingPriceUsd: Number(values.askingPriceUsd),
						profitMarginPct: Number(values.profitMarginPct),
					}),
				},
			);

			const data = (await response.json().catch(() => null)) as {
				url?: string;
				error?: string;
			} | null;

			if (!response.ok || !data?.url) {
				throw new Error(data?.error || "Failed to start checkout");
			}

			trackEvent("startup_listing_checkout_started", { plan: selectedPlan });
			window.location.href = data.url;
		} catch (error) {
			toast.error(
				error instanceof Error ? error.message : "Failed to start checkout",
			);
		}
	};

	const provider = form.watch("provider") as RevenueProviderId;
	const providerConfig = REVENUE_PROVIDER_CONFIG[provider];
	const isLoading = form.formState.isSubmitting;

	return (
		<DialogPrimitive.Root open={open} onOpenChange={handleOpenChange}>
			<DialogPrimitive.Portal>
				<DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
				<DialogPrimitive.Content className="fixed left-[50%] top-[50%] z-50 w-full max-w-2xl translate-x-[-50%] translate-y-[-50%] px-4 duration-200 outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95">
					<DialogPrimitive.Title className="sr-only">
						Sell your startup on AgentMRR
					</DialogPrimitive.Title>
					<DialogPrimitive.Description className="sr-only">
						List your startup for sale and reach buyers actively looking to
						acquire AI-native startups.
					</DialogPrimitive.Description>
					<div className="max-h-[90vh] overflow-y-auto border border-border bg-card text-card-foreground">
						<div className="flex items-start justify-between gap-4 border-b border-border px-5 py-4">
							<div className="space-y-1.5">
								<h2 className="font-mono text-lg tracking-[-0.01em] text-foreground">
									Sell your startup
								</h2>
								<p className="font-mono text-sm text-muted-foreground">
									Reach{" "}
									<span className="text-foreground">
										120,000+ monthly visitors
									</span>{" "}
									actively looking to buy startups.
								</p>
							</div>
							<DialogPrimitive.Close className="-mr-2 -mt-1 inline-flex size-10 shrink-0 items-center justify-center font-mono text-sm text-muted-foreground transition-[color,transform] hover:text-brand active:scale-[0.96] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand">
								<X className="size-3.5" />
								<span className="sr-only">Close</span>
							</DialogPrimitive.Close>
						</div>

						<Form {...form}>
							<form onSubmit={form.handleSubmit(onSubmit)} noValidate>
								<div className="space-y-6 p-5">
									<FormField
										control={form.control}
										name="provider"
										render={({ field }) => (
											<FormItem>
												<FormLabel>Choose your payment provider</FormLabel>
												<FormControl>
													<RevenueProviderSelect
														value={field.value as RevenueProviderId}
														onChange={field.onChange}
													/>
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>

									<div className="space-y-4">
										<FormField
											control={form.control}
											name="apiKey"
											render={({ field }) => (
												<FormItem>
													<FormLabel>1. {providerConfig.keyLabel}</FormLabel>
													<FormControl>
														<BrandInput
															placeholder={providerConfig.placeholder}
															{...field}
														/>
													</FormControl>
													<FormMessage />
												</FormItem>
											)}
										/>

										<div className="border border-border bg-muted/30 p-3.5">
											<a
												href={providerConfig.linkUrl}
												target="_blank"
												rel="noopener noreferrer"
												className="inline-flex items-center gap-1.5 font-mono text-sm uppercase tracking-[0.12em] text-foreground transition-colors hover:text-brand hover:underline"
											>
												{providerConfig.linkLabel}
												<ExternalLink className="size-3" />
											</a>
											<ol className="mt-2.5 space-y-0.5 font-mono text-sm text-muted-foreground">
												{providerConfig.instructions.map(
													(instruction, index) => (
														<li key={instruction}>
															{String(index + 1)} · {instruction}
														</li>
													),
												)}
											</ol>
										</div>
									</div>

									<FormField
										control={form.control}
										name="askingPriceUsd"
										render={({ field }) => (
											<FormItem>
												<FormLabel>
													2. Asking price in USD{" "}
													<span className="text-red-500">*</span>
												</FormLabel>
												<FormControl>
													<BrandInput
														type="number"
														inputMode="numeric"
														min={0}
														placeholder="e.g. 50000"
														{...field}
													/>
												</FormControl>
												<FormMessage />
												<div className="mt-3 flex gap-2.5 border border-brand/40 bg-brand/5 p-3.5">
													<Info className="mt-0.5 size-4 shrink-0 text-brand" />
													<p className="font-mono text-sm leading-relaxed text-muted-foreground">
														Startups are ranked by their valuation multiple
														(Price / Annual Revenue). Lower multiples rank
														higher. We recommend 2-4x your annual revenue (e.g.
														if making $10k/year, ask for $20k-$40k).
													</p>
												</div>
											</FormItem>
										)}
									/>

									<FormField
										control={form.control}
										name="profitMarginPct"
										render={({ field }) => (
											<FormItem>
												<FormLabel>
													3. Profit margin last 30 days (%){" "}
													<span className="text-red-500">*</span>
												</FormLabel>
												<FormControl>
													<BrandInput
														type="number"
														inputMode="numeric"
														min={0}
														max={100}
														placeholder="e.g. 65"
														{...field}
													/>
												</FormControl>
												<FormMessage />
												<p className="mt-2 font-mono text-sm text-muted-foreground">
													65 means you made $65 in profit for every $100 in
													revenue.
												</p>
											</FormItem>
										)}
									/>

									<div className="border-t border-border pt-6">
										<h3 className="font-mono text-base text-foreground">
											Choose a listing plan
										</h3>
										<div className="mt-4 grid gap-3 sm:grid-cols-3">
											{STARTUP_LISTING_PLANS.map((plan) => {
												const isSelected = selectedPlan === plan.id;
												return (
													<button
														key={plan.id}
														type="button"
														onClick={() => setSelectedPlan(plan.id)}
														aria-pressed={isSelected}
														className={cn(
															"flex flex-col gap-4 border p-4 text-left transition-colors",
															isSelected
																? "border-brand bg-brand/5"
																: "border-border bg-muted/20 hover:border-brand/40",
														)}
													>
														<div className="space-y-1">
															<div className="flex items-center justify-between gap-2">
																<span className="font-mono text-xl text-foreground">
																	${plan.priceUsd}
																</span>
																{plan.featured && (
																	<span className="bg-brand/15 px-1.5 py-0.5 font-mono text-xs uppercase tracking-[0.12em] text-brand">
																		Popular
																	</span>
																)}
															</div>
															<span className="font-mono text-sm text-muted-foreground">
																one-time
															</span>
														</div>
														<ul className="space-y-2">
															{plan.features.map((feature) => {
																const Icon = PLAN_FEATURE_ICONS[feature.icon];
																return (
																	<li
																		key={feature.label}
																		className="flex items-center gap-2 font-mono text-sm text-foreground"
																	>
																		<Icon className="size-3.5 shrink-0 text-muted-foreground" />
																		{feature.label}
																	</li>
																);
															})}
														</ul>
													</button>
												);
											})}
										</div>
									</div>
								</div>

								<div className="flex items-center justify-end gap-3 border-t border-border px-5 py-3">
									<BrandButton
										type="submit"
										size="lg"
										disabled={isLoading || !selectedPlan}
									>
										{isLoading ? (
											<>
												<Loader2 className="size-3.5 animate-spin" />
												Redirecting
											</>
										) : (
											"List for sale"
										)}
									</BrandButton>
								</div>
							</form>
						</Form>
					</div>
				</DialogPrimitive.Content>
			</DialogPrimitive.Portal>
		</DialogPrimitive.Root>
	);
}
